'use client';

import { useRef, useEffect, type ReactNode } from 'react';
import { useInViewport } from 'ahooks';
import { track } from '@/utils/track';
import { useThrottleByRef } from '../hooks/use-throttle-expose';
import { useModalOverlay } from '../hooks/use-modal-overlay';
import useFn from '@/hooks/use-fn';
import { getCasinoGameDetail } from '@/features/casino/services/detail';
import { GAME_TYPE, GAME_TYPE_MAP } from '../const/game';
import { useSearchParams } from 'next/navigation';

export interface GameTrackingData {
  id?: string | number;
  // 基础信息
  game_id?: string | number;
  game_name?: string;
  name?: string;
  // 详细游戏信息（可选，用于 casino 游戏）
  vendor_id?: string;
  provider_id?: string | number;
  game_code?: string;
  game_type?: string | number;
  game_category_id?: string;
  game_category_name?: string;
  vendor?: string;
  provider_name?: string;
  provider?: string;
}

interface GameCardWithTrackingProps {
  // 游戏数据
  item: GameTrackingData;
  index: number;
  // 页面和模块信息
  currentPage?: string;
  currentModule?: string;
  // 节流等待时间（毫秒）
  throttleWait?: number;
  // 生成唯一 key 的函数（用于节流）
  getThrottleKey?: (item: GameTrackingData, index: number) => string;
  // 是否启用点击埋点（默认 true）
  enableClickTracking?: boolean;
  // 是否启用曝光埋点（默认 true）
  enableExposureTracking?: boolean;
  // 点击时的额外处理函数（可选，用于获取游戏详情等）
  onBeforeClickTrack?: (item: GameTrackingData) => Promise<Partial<GameTrackingData>>;
  // Render props
  children: (props: {
    cardRef: React.RefObject<HTMLDivElement | null>;
    handleClick: () => void | Promise<void>;
  }) => ReactNode;
}

export default function GameCardWithTracking({
  item,
  index,
  currentPage = 'Home',
  currentModule = 'casino',
  throttleWait = 30 * 1000,
  getThrottleKey,
  enableClickTracking = true,
  enableExposureTracking = true,
  children,
}: GameCardWithTrackingProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isInViewport] = useInViewport(cardRef);
  const throttleExposure = useThrottleByRef({ wait: throttleWait });
  const hasModalOverlay = useModalOverlay();
  const searchParams = useSearchParams();
  const fromPosition = searchParams.get('trackSource') || '';
  const fromContainerSet = searchParams.get('containerSetName') || '';

  // 获取游戏详情的函数（用于点击埋点）
  const getGameDetailForTracking = async (trackingItem: GameTrackingData): Promise<Partial<GameTrackingData>> => {
    try {
      const casinoGameDetail = await getCasinoGameDetail({ game_type: '1', game_id: trackingItem?.id?.toString() || trackingItem.game_id?.toString() || '' });
      return {
        vendor_id: casinoGameDetail?.game_info?.vendor_id || '',
        vendor: casinoGameDetail?.game_info?.vendor || '',
        game_code: casinoGameDetail?.game_info?.game_code || '',
        game_type: casinoGameDetail?.game_info?.game_type || '',
        game_category_id: casinoGameDetail?.game_info?.game_category_id || '',
        game_category_name: casinoGameDetail?.game_info?.game_category_name || '',
        game_name: casinoGameDetail?.game_info?.game_name || '',
      };
    } catch (error) {
      console.error('Failed to get game detail:', error);
      return {};
    }
  };

  // 生成默认的节流 key
  const defaultGetThrottleKey = (item: GameTrackingData, index: number) => {
    if (item.game_id) {
      return `${currentModule}_${currentPage}_game_${item.game_id}_${index}`;
    }
    return `${currentModule}_${currentPage}_game_${index}`;
  };

  const throttleKeyFn = getThrottleKey || defaultGetThrottleKey;

  const gameType = {
    [GAME_TYPE.Casino]: GAME_TYPE_MAP[GAME_TYPE.Casino],
    [GAME_TYPE.Fantasy]: GAME_TYPE_MAP[GAME_TYPE.Fantasy],
    [GAME_TYPE.Rummy]: GAME_TYPE_MAP[GAME_TYPE.Rummy],
    [GAME_TYPE.Sports]: GAME_TYPE_MAP[GAME_TYPE.Sports],
  };
  // 处理曝光埋点
  useEffect(() => {
    if (!enableExposureTracking) return;

    const key = throttleKeyFn(item, index);
    const shouldTrack = isInViewport && item && !hasModalOverlay;

    // 对于 casino 游戏，需要 game_id；对于其他游戏，需要其他标识
    const hasValidIdentifier = item.game_id || item.game_type || item.game_code || item?.id;

    if (shouldTrack && hasValidIdentifier) {
      throttleExposure(`${key}_${currentModule}`, async () => {
        try {
          const trackingData = {
            current_page: currentPage,
            current_module: currentModule?.toLowerCase() || currentModule,
            vendor_id: item?.vendor_id || item?.provider_id || '',
            game_code: item?.game_code || '',
            game_type: gameType[item?.game_type as keyof typeof gameType] || '',
            game_category_id: item?.game_category_id || '',
            game_id: String(item?.game_id || item?.id || ''),
            game_name: item?.game_name || item?.name || '',
            sort: index + 1,
            from_position: fromPosition,
            from_container_set: fromContainerSet,
          };
          track('game_exposure', trackingData);
        } catch (error) {
          console.error('Failed to track game exposure:', error);
        }
      });
    }
  }, [
    isInViewport,
    hasModalOverlay,
    item,
    index,
    throttleExposure,
    currentPage,
    currentModule,
    enableExposureTracking,
    throttleKeyFn,
  ]);

  // 处理点击埋点
  const handleClick = useFn(async () => {
    if (!enableClickTracking) return;

    try {
      let trackingData: Partial<GameTrackingData> = { ...item };
      let additionalData: Partial<GameTrackingData> = {};
      // 如果是 casino 游戏，则需要获取游戏详情
      if (item?.game_type === GAME_TYPE.Casino) {
        additionalData = await getGameDetailForTracking(item);
      }
      trackingData = { ...additionalData, ...trackingData };
      track('game_click', {
        current_page: currentPage,
        current_module: currentModule?.toLowerCase() || currentModule,
        vendor_id: trackingData?.vendor_id || trackingData?.provider_id || '',
        vendor: trackingData?.vendor || trackingData?.provider_name || trackingData?.provider || '',
        game_code: trackingData?.game_code || '',
        game_type: gameType[trackingData?.game_type as keyof typeof gameType] || '',
        game_category_id: trackingData?.game_category_id || '',
        game_category_name: trackingData?.game_category_name || '',
        game_id: (item?.id || item?.game_id || trackingData?.game_id)?.toString() || '',
        game_name: trackingData?.game_name || item?.game_name || '',
        sort: index + 1,
        from_position: fromPosition,
        from_container_set: fromContainerSet,
      });
    } catch (error) {
      console.error('Failed to track game click:', error);
    }
  });

  return <>{children({ cardRef, handleClick })}</>;
}

